import {
  DatePicker,
  DateRangePicker,
  Divider,
  Flex,
  IllustratedMessage,
  Notice,
  Progress,
  Scrollbar,
  Select,
  Stack,
  TextArea,
  commaValueFormatter,
} from '@eleveo/components-react';
import { SearchNoneIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { parseAbsolute, toCalendarDate, toCalendarDateTime, today } from '@internationalized/date';
import clsx from 'clsx';
import { clone } from 'ramda';
import React, { Key, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { useField, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { AgentShiftTile } from './AgentShiftTile';
import { ShiftSwapRequestFormValues } from './types';
import {
  RECIPIENT_SHIFT_HAS_ALREADY_STARTED_KEY,
  isShiftAlreadyStarted,
  isShiftSwappable,
  max255chars,
  minDate,
  required,
  startBeforeEnd,
} from './validations';
import { useAuth } from '../../app/AuthorizationProvider';
import {
  AgentShift,
  RequestSubtype,
  ScheduleElement,
  ShiftOverview,
  ShiftSwapRecipientGroup,
  ShiftSwapRequestSubtype,
  useGetShiftOverviewsForRequesterQuery,
  useGetSuitableRecipientsQuery,
  useGetSuitableShiftsForSwapQuery,
} from '../../graphql/components';
import { getShiftKey } from '../../scenes/Scheduling/AgentsView/utils';
import { DateRange } from '../../types';
import { formatNameOfPerson } from '../../utils/helpers';
import { composeValidators } from '../../utils/validations/composeValidators';
import { DateFormatter } from '../DateFormatter';
import { Center } from '../LayoutNext/components/Center';

const recipientGroupsToSelect = [ShiftSwapRecipientGroup.All, ShiftSwapRecipientGroup.Team];

type Props = {
  scheduleElements: ScheduleElement[];
};

export const CreateShiftSwapRequestModalBody: React.FC<Props> = ({ scheduleElements }) => {
  const { userId: agentId, timezone } = useAuth();
  const { t } = useTranslation();
  const formatter = useDateFormatter({ dateStyle: 'short', timeZone: timezone });
  const form = useForm<ShiftSwapRequestFormValues>();

  const [selectedDateShiftOverview, setSelectedDateShiftOverview] = useState<ShiftOverview | undefined>(undefined);
  const [recipientGroupFilter, setRecipientGroupFilter] = useState<ShiftSwapRecipientGroup>(
    ShiftSwapRecipientGroup.All
  );
  const [recipientIdsFilter, setRecipientIdsFilter] = useState<string[]>([]);
  const [rangeOfRecipientsShiftsFilter, setRangeOfRecipientsShiftsFilter] = useState<DateRange>(null);
  const [alreadyStartedRecipientsShiftsKeys, setAlreadyStartedRecipientsShiftsKeys] = useState<Record<string, boolean>>(
    {}
  );

  const dateFromField = useField<ShiftSwapRequestFormValues['dateFromForSuitableShifts']>('dateFromForSuitableShifts', {
    validate: composeValidators(required, minDate(today(timezone))),
  });

  useEffect(() => {
    if (dateFromField.meta.invalid) {
      // when date before today selected by text input field, the query for shift overview should be skipped, therefore the previous value needs to be emptied
      setSelectedDateShiftOverview(undefined);
    }
  }, [dateFromField.meta.invalid]);

  const {
    input: { value: isSelectedDaysShiftSwappable, onChange: setSelectedDaysShiftSwappable },
    meta: isSelectedDaysShiftSwappableMeta,
  } = useField<ShiftSwapRequestFormValues['isSelectedDaysShiftSwappable']>('isSelectedDaysShiftSwappable', {
    validate: composeValidators(isShiftAlreadyStarted(ShiftSwapRequestSubtype.Requester, timezone), isShiftSwappable),
  });

  const onDateFromChange = (newDateFrom: ShiftSwapRequestFormValues['dateFromForSuitableShifts']) => {
    if (dateFromField.input.value?.toDate?.(timezone).getTime() !== newDateFrom?.toDate?.(timezone).getTime()) {
      // when user select new date (different from current one), reset previous value until new shift overview data are loaded
      setSelectedDaysShiftSwappable(undefined);
      setAlreadyStartedRecipientsShiftsKeys({});
    }
    dateFromField.input.onChange(newDateFrom);
  };

  const dateFrom = dateFromField.input.value || null;
  const dateFromISOString = dateFrom ? dateFrom.toDate(timezone).toISOString() : '';
  const endOfDateFromISOString = dateFrom ? dateFrom.add({ days: 1 })?.toDate(timezone).toISOString() : '';

  const {
    input: { onChange: changeRequesterShiftFromDate },
  } = useField<ShiftSwapRequestFormValues['requesterShift']['from']>('requesterShift.from', {
    validate: required,
  });

  const {
    input: {
      value: { recipientId: shiftToSwapRecipientId, from: shiftToSwapFromDate } = {},
      onChange: onShiftToSwapChange,
    },
    meta: recipientShiftMeta,
  } = useField<ShiftSwapRequestFormValues['recipientShift']>('recipientShift', {
    validate: composeValidators(required, isShiftAlreadyStarted(ShiftSwapRequestSubtype.Recipient, timezone)),
  });

  const { loading: isLoadingShiftOverviewsForRequesterData } = useGetShiftOverviewsForRequesterQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: (data) => {
      const startOfShift = data.shiftOverviewsForRequester.shiftOverview?.[0]?.dateRange?.from;
      changeRequesterShiftFromDate(startOfShift && toCalendarDateTime(parseAbsolute(startOfShift, timezone)));
      setSelectedDateShiftOverview(data.shiftOverviewsForRequester?.shiftOverview?.[0]);
      setSelectedDaysShiftSwappable(data.shiftOverviewsForRequester?.shiftOverview?.[0]?.swappable);

      if (
        !data.shiftOverviewsForRequester.shiftOverview?.[0].swappable &&
        shiftToSwapRecipientId &&
        shiftToSwapFromDate
      ) {
        // reset selected shift to swap as it's no longer available due to skipping of getAvailableShifts request caused by not swappable shift
        onShiftToSwapChange(undefined);
      }
    },
    onError: () => {
      // reset states on error
      setSelectedDateShiftOverview(undefined);
      changeRequesterShiftFromDate(undefined);
      setSelectedDaysShiftSwappable(undefined);
    },
    skip: !dateFrom || dateFromField.meta.invalid,
    variables: {
      requesterId: agentId,
      from: dateFromISOString,
      to: endOfDateFromISOString,
    },
  });

  const selectedDateShiftFromDate = selectedDateShiftOverview?.dateRange?.from
    ? toCalendarDateTime(parseAbsolute(selectedDateShiftOverview.dateRange.from, timezone))
    : undefined;

  const messageField = useField<ShiftSwapRequestFormValues['message']>('message', {
    validate: max255chars,
  });

  const todayFormatted = formatter.format(today(timezone).toDate(timezone));

  const selectedDateRangeErrorMessage = useMemo(() => {
    if (!rangeOfRecipientsShiftsFilter) {
      // range is not mandatory, therefore this is a valid scenario
      return undefined;
    }

    const errorMessage = startBeforeEnd()(rangeOfRecipientsShiftsFilter);

    if (errorMessage) {
      return t(errorMessage);
    }

    if (
      today(timezone).compare(rangeOfRecipientsShiftsFilter.start) > 0 ||
      today(timezone).compare(rangeOfRecipientsShiftsFilter.end) > 0
    ) {
      return t('validation.invalidDateRangeLessThanMin', {
        minValue: todayFormatted,
      });
    }

    return undefined;
  }, [rangeOfRecipientsShiftsFilter, t, timezone, todayFormatted]);

  const { data: { suitableShiftsForSwap: suitableShiftsData } = {}, loading: isLoadingSuitableShiftsData } =
    useGetSuitableShiftsForSwapQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
      onCompleted: (data) => {
        const fromDate = shiftToSwapFromDate?.toDate(timezone).toISOString();

        if (
          shiftToSwapRecipientId &&
          fromDate &&
          !data.suitableShiftsForSwap.find(
            (shift) => shift.agent.id === shiftToSwapRecipientId && fromDate === shift.dateRange.from
          )
        ) {
          // reset selected shift to swap if it's no longer available after the refetch
          onShiftToSwapChange(undefined);
        }
      },
      onError: () => {
        // reset form state on error
        onShiftToSwapChange(undefined);
        setAlreadyStartedRecipientsShiftsKeys({});
      },
      skip:
        !dateFrom ||
        !isSelectedDaysShiftSwappable ||
        !!selectedDateRangeErrorMessage ||
        isSelectedDaysShiftSwappableMeta.error,
      variables: {
        requesterId: agentId,
        shiftDate: dateFromISOString,
        recipientGroup: recipientGroupFilter,
        recipients: recipientIdsFilter.length ? recipientIdsFilter.toString() : undefined,
        shiftsFrom: rangeOfRecipientsShiftsFilter?.start?.toDate(timezone).toISOString(),
        shiftsTo: rangeOfRecipientsShiftsFilter?.end?.add({ days: 1 }).toDate(timezone).toISOString(), // end of day value
      },
    });

  useEffect(() => {
    if (isLoadingSuitableShiftsData) {
      return () => null;
    }

    const checkIfShiftHasStarted = () => {
      setAlreadyStartedRecipientsShiftsKeys((previousState) => {
        const alreadyStartedShifts = clone(previousState);
        const currentRecipientShiftState = form.getState().values.recipientShift;

        suitableShiftsData?.forEach((shift) => {
          const shiftKey = getShiftKey(shift);

          if (
            isShiftAlreadyStarted(ShiftSwapRequestSubtype.Recipient, timezone)(undefined, {
              recipientShift: {
                from: toCalendarDateTime(parseAbsolute(shift.dateRange.from, timezone)),
              },
            }) &&
            !alreadyStartedShifts[shiftKey]
          ) {
            alreadyStartedShifts[shiftKey] = true;
          }
        });

        // trigger re-validation of currently selected recipient's shift
        if (currentRecipientShiftState?.from && currentRecipientShiftState?.recipientId) {
          form.change('recipientShift', currentRecipientShiftState);
        }

        return alreadyStartedShifts;
      });
    };

    // initial trigger
    checkIfShiftHasStarted();

    const interval = setInterval(() => {
      checkIfShiftHasStarted();
    }, 20000); // should check 3 times within every minute - no need to align with start of minute

    return () => {
      clearInterval(interval);
    };
  }, [form, isLoadingSuitableShiftsData, suitableShiftsData, timezone]);

  useEffect(() => {
    if (!shiftToSwapFromDate || !shiftToSwapRecipientId) {
      return;
    }

    setAlreadyStartedRecipientsShiftsKeys((previousState) => {
      const alreadyStartedShifts = clone(previousState);
      const shiftKey = getShiftKey({
        agent: { id: shiftToSwapRecipientId },
        dateRange: { from: shiftToSwapFromDate.toDate(timezone).toISOString() },
      } as unknown as AgentShift);

      if (
        isShiftAlreadyStarted(ShiftSwapRequestSubtype.Recipient, timezone)(undefined, {
          recipientShift: {
            from: shiftToSwapFromDate,
          },
        }) &&
        !alreadyStartedShifts[shiftKey]
      ) {
        alreadyStartedShifts[shiftKey] = true;
      }

      return alreadyStartedShifts;
    });
  }, [shiftToSwapFromDate, shiftToSwapRecipientId, recipientShiftMeta.error, timezone]);

  const suitableShiftsByDays = useMemo(() => {
    const daysWithShifts: Record<number, AgentShift[]> = {};

    suitableShiftsData?.forEach((shiftData) => {
      const fromDate = toCalendarDate(parseAbsolute(shiftData.dateRange.from, timezone));
      const fromDateNumber = fromDate.toDate(timezone).getTime();

      if (!daysWithShifts[fromDateNumber]) {
        daysWithShifts[fromDateNumber] = [];
      }

      daysWithShifts[fromDateNumber].push(shiftData); // considering shifts were already sorted by start time
    });

    return daysWithShifts;
  }, [suitableShiftsData, timezone]);

  const { data: { suitableRecipients } = { suitableRecipients: [] }, loading: isLoadingSuitableRecipients } =
    useGetSuitableRecipientsQuery({
      fetchPolicy: 'network-only',
      variables: { requesterId: agentId },
    });

  const recipientsOptions = useMemo(
    () => [
      ...recipientGroupsToSelect.map((groupId) => {
        const optionsName = t(`schedule.request.shiftSwapRecipientGroup.${groupId}`);

        return {
          id: groupId,
          name: optionsName,
        };
      }),
      {
        children: suitableRecipients.map((agent) => ({
          id: agent.id,
          name: formatNameOfPerson(agent.firstName, agent.lastName),
        })),
        id: 'specific-agents-selection',
        name: t('person.availablePeople'), // should be changed to 'aria-label' when UI-337 is in place (and UI kit dependency is upgraded)
      },
    ],
    [suitableRecipients, t]
  );

  const onRecipientsOptionsChange = (selectedOptions: Set<Key>) => {
    let options = Array.from(selectedOptions);

    if (options.length > 1) {
      options = options.filter((option) => option !== recipientGroupFilter);
    }

    const newRecipientGroups = options.filter((option) =>
      recipientGroupsToSelect.includes(option as ShiftSwapRecipientGroup)
    ) as ShiftSwapRecipientGroup[];

    if (newRecipientGroups.length) {
      setRecipientGroupFilter(newRecipientGroups[0]);
      setRecipientIdsFilter([]);
    } else {
      setRecipientGroupFilter(ShiftSwapRecipientGroup.Specific);
      setRecipientIdsFilter(options as string[]);
    }
  };

  const timeFormatterOptions = {
    timeStyle: 'short' as const,
    timeZone: timezone,
  };

  const isLoadingData =
    isLoadingShiftOverviewsForRequesterData || isLoadingSuitableShiftsData || isLoadingSuitableRecipients;

  const hasRecipientShiftAlreadyStartedError = recipientShiftMeta.error === RECIPIENT_SHIFT_HAS_ALREADY_STARTED_KEY;

  return (
    <>
      <Stack spacing={20} className="mt-10 items-baseline">
        <DatePicker
          {...dateFromField.input}
          errorMessage={t(dateFromField.meta.error || isSelectedDaysShiftSwappableMeta.error, {
            minValue: todayFormatted,
          })}
          granularity="day"
          isRequired
          label={t('date.date')}
          onChange={onDateFromChange}
          testId="agent-portal:monthly-view:create-shift-swap-request:date"
          minValue={today(timezone)}
          validationState={
            (dateFromField.meta.touched && dateFromField.meta.invalid) ||
            (!isSelectedDaysShiftSwappableMeta.pristine && isSelectedDaysShiftSwappableMeta.invalid)
              ? 'invalid'
              : undefined
          }
          value={dateFrom}
        />
        {!!selectedDateShiftFromDate && !!selectedDateShiftOverview?.dateRange?.to && (
          <>
            <div data-test-id="agent-portal:monthly-view:create-shift-swap-request:offered-shift:start-time">
              {t('time.start')}:{' '}
              <DateFormatter className="font-bold" options={timeFormatterOptions}>
                {selectedDateShiftFromDate.toDate(timezone)}
              </DateFormatter>
            </div>

            <div data-test-id="agent-portal:monthly-view:create-shift-swap-request:offered-shift:end-time">
              {t('time.end')}:{' '}
              <DateFormatter className="font-bold" options={timeFormatterOptions}>
                {toCalendarDateTime(parseAbsolute(selectedDateShiftOverview.dateRange.to, timezone)).toDate(timezone)}
              </DateFormatter>
            </div>
          </>
        )}
      </Stack>

      <TextArea
        className="w-full"
        errorMessage={t(messageField.meta.error, { characters: 255 })}
        label={t('schedule.request.message')}
        onBlur={messageField.input.onBlur}
        onFocus={messageField.input.onFocus}
        onChange={messageField.input.onChange}
        testId="agent-portal:monthly-view:create-shift-swap-request:message"
        value={messageField.input.value ?? ''}
        validationState={messageField.meta.touched && messageField.meta.invalid ? 'invalid' : undefined}
      />

      {/* The starting part of grey-ish area */}
      <Stack className="p-20 bg-grey-100 border border-grey-300 border-b-0 rounded-t -mb-20" spacing={20}>
        <Select
          className="w-1/2"
          isSearchable
          label={t('schedule.request.shiftOffers')}
          onSelectionChange={onRecipientsOptionsChange}
          options={recipientsOptions}
          selectionMode="multiple"
          selectedKeys={
            recipientGroupFilter === ShiftSwapRecipientGroup.Specific ? recipientIdsFilter : [recipientGroupFilter]
          }
          testId="agent-portal:monthly-view:create-shift-swap-request:filter:shift-offers"
          valueFormatter={commaValueFormatter}
        />
        <DateRangePicker
          className="w-1/2"
          label={t('date.dateRange')}
          minValue={today(timezone)}
          onChange={setRangeOfRecipientsShiftsFilter}
          testId="agent-portal:monthly-view:create-shift-swap-request:filter:date-range"
          value={rangeOfRecipientsShiftsFilter}
          errorMessage={selectedDateRangeErrorMessage}
          validationState={selectedDateRangeErrorMessage ? 'invalid' : undefined}
        />
      </Stack>

      {isLoadingData && (
        <Flex className="flex-1 bg-grey-100 border border-grey-300 border-t-0 border-b-0 pt-10 pb-25 px-20">
          <Progress className="m-auto py-20" />
        </Flex>
      )}

      {!isLoadingData && !suitableShiftsData?.length && (
        <Flex column className="flex-1 bg-grey-100 border border-grey-300 border-t-0 border-b-0 px-20">
          <Flex className="pb-10">
            <Divider className="w-full" />
          </Flex>
          <Center className="w-full pb-15 flex-1">
            <IllustratedMessage
              icon={<SearchNoneIcon />}
              heading={t('schedule.request.noSuitableShiftFound')}
              description={t('schedule.request.noSuitableShiftFoundDescription')}
              isSmall
            />
          </Center>
        </Flex>
      )}

      {!isLoadingData && !!suitableShiftsData?.length && suitableShiftsByDays && (
        <Scrollbar className="flex-1 bg-grey-100 border border-grey-300 border-t-0 border-b-0">
          <Flex column gap={20} wrap className="px-20">
            {Object.keys(suitableShiftsByDays).map((key) => {
              const suitableShiftsByDayKey = Number.parseInt(key, 10);

              return (
                <Flex column className="w-full" key={suitableShiftsByDayKey}>
                  <Divider className="mb-20 w-full" />
                  <Flex
                    className="w-full"
                    gap={20}
                    testId={`agent-portal:monthly-view:create-shift-swap-request:suitable-shifts-from-day-${new Date(
                      suitableShiftsByDayKey
                    ).toISOString()}`}
                    wrap
                  >
                    {suitableShiftsByDays[suitableShiftsByDayKey].map((suitableShift) => {
                      // There is no specific ID attached to shifts, therefore we need to combine both agentId and from date to determine the shift itself
                      const isSameAgentAsRecipient = shiftToSwapRecipientId === suitableShift.agent.id;
                      const isSameFromDateAsInRecipientsShift =
                        shiftToSwapFromDate?.toDate(timezone).getTime() ===
                        toCalendarDateTime(parseAbsolute(suitableShift.dateRange.from, timezone))
                          .toDate(timezone)
                          .getTime();
                      const shiftKey = getShiftKey(suitableShift);
                      const isSelected = isSameAgentAsRecipient && isSameFromDateAsInRecipientsShift;

                      return (
                        <AgentShiftTile
                          key={shiftKey}
                          className={clsx('shift-swap-request__agent-shift-tile', {
                            'shift-swap-request__agent-shift-tile--disabled-and-selected':
                              alreadyStartedRecipientsShiftsKeys[shiftKey] && isSelected,
                          })}
                          {...suitableShift}
                          isDisabled={alreadyStartedRecipientsShiftsKeys[shiftKey]}
                          isSelected={isSelected}
                          onSelect={(recipientId, from) =>
                            onShiftToSwapChange(recipientId && from ? { recipientId, from } : undefined)
                          }
                          shiftSubtype={RequestSubtype.Requester}
                          scheduleElements={scheduleElements}
                          testIdSlug="agent-portal:monthly-view:create-shift-swap-request:recipient"
                        />
                      );
                    })}
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        </Scrollbar>
      )}
      {/* The ending part of grey-ish area */}
      <div className="-mt-20 pt-20 bg-grey-100 border border-grey-300 border-t-0 rounded-b" />

      {hasRecipientShiftAlreadyStartedError && (
        <Notice
          className="-my-10"
          color="error"
          testId="agent-portal:monthly-view:create-shift-swap-request:recipient:shift-is-already-in-progress"
        >
          {t(recipientShiftMeta.error)}
        </Notice>
      )}

      <Notice color="warning">{t('schedule.request.newShiftSwapRequestNotice')}</Notice>
    </>
  );
};
